import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import { Building2, Plus, Users, Stethoscope, Calendar, MapPin, Phone, Mail, Pencil, Trash2, X, UserPlus, ChevronDown, ChevronUp, Eye, EyeOff, RotateCcw, LogIn, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function ChatToggle({ clinicId, token }) {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/chat/settings/${clinicId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setEnabled(d.chat_enabled); setLoading(false); })
      .catch(() => setLoading(false));
  }, [clinicId, token]);

  const toggle = async () => {
    const newVal = !enabled;
    try {
      await fetch(`${API}/api/chat/settings/${clinicId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_enabled: newVal })
      });
      setEnabled(newVal);
      toast.success(`Chat ${newVal ? 'attivata' : 'disattivata'}`);
    } catch { toast.error('Errore'); }
  };

  if (loading) return null;

  return (
    <div className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-4 py-3" data-testid="chat-toggle">
      <div className="flex items-center gap-2">
        <MessageCircle className="w-4 h-4 text-emerald-500" />
        <span className="text-sm font-medium text-slate-700">Chat Interna</span>
        <span className="text-xs text-slate-400">(medici + receptionist)</span>
      </div>
      <button onClick={toggle}
        className={`relative w-10 h-5 rounded-full transition-colors ${enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
        data-testid="chat-toggle-btn">
        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}

export default function Clinics() {
  const { token, user, refreshClinics } = useAuth();
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editClinic, setEditClinic] = useState(null);
  const [expandedClinic, setExpandedClinic] = useState(null);
  const [showAddUser, setShowAddUser] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', city: '', phone: '', email: '', phone_number: '' });
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'receptionist' });
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [allMyNumbers, setAllMyNumbers] = useState([]);

  const fetchClinics = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/clinics`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setClinics(Array.isArray(data) ? data : []);
    } catch { toast.error('Errore caricamento cliniche'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchClinics(); fetchNumbers(); }, [fetchClinics]);

  const fetchNumbers = async () => {
    try {
      const [avail, all] = await Promise.all([
        fetch(`${API}/api/phone-numbers/available`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API}/api/phone-numbers`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json())
      ]);
      setAvailableNumbers(Array.isArray(avail) ? avail : []);
      setAllMyNumbers(Array.isArray(all) ? all : []);
    } catch { /* ignore */ }
  };

  const handleSave = async () => {
    const url = editClinic ? `${API}/api/clinics/${editClinic.id}` : `${API}/api/clinics`;
    const method = editClinic ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(editClinic ? 'Clinica aggiornata!' : 'Clinica creata!');
        setShowCreate(false); setEditClinic(null);
        setForm({ name: '', address: '', city: '', phone: '', email: '', phone_number: '', transfer_number: '' });
        fetchClinics();
        fetchNumbers();
        if (refreshClinics) refreshClinics(); // Update clinic selector in sidebar
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Errore');
      }
    } catch { toast.error('Errore di rete'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eliminare "${name}" e TUTTI i dati associati? Questa azione non può essere annullata.`)) return;
    try {
      const res = await fetch(`${API}/api/clinics/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success('Clinica eliminata'); fetchClinics(); }
    } catch { toast.error('Errore'); }
  };

  const handleAddUser = async (clinicId) => {
    try {
      const res = await fetch(`${API}/api/auth/create-clinic-user`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(userForm)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Utente creato!');
        setShowAddUser(null);
        setUserForm({ name: '', email: '', password: '', role: 'receptionist' });
        fetchClinics();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Errore');
      }
    } catch { toast.error('Errore di rete'); }
  };

  const handleUpdateUser = async (clinicId, userId) => {
    try {
      const body = { name: editingUser.name, role: editingUser.role };
      if (editingUser.newPassword) body.password = editingUser.newPassword;
      const res = await fetch(`${API}/api/clinics/${clinicId}/users/${userId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.ok) { toast.success('Utente aggiornato'); setEditingUser(null); fetchClinics(); }
      else { const err = await res.json(); toast.error(err.detail || 'Errore'); }
    } catch { toast.error('Errore di rete'); }
  };

  const handleDeleteUser = async (clinicId, userId, userName) => {
    if (!window.confirm(`Eliminare l'utente ${userName}? Questa azione è irreversibile.`)) return;
    try {
      const res = await fetch(`${API}/api/clinics/${clinicId}/users/${userId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success('Utente eliminato'); fetchClinics(); }
      else { const err = await res.json(); toast.error(err.detail || 'Errore'); }
    } catch { toast.error('Errore di rete'); }
  };

  const handleResetPassword = async (clinicId, userId, userName) => {
    if (!window.confirm(`Reimpostare la password per ${userName}? Verrà inviata una nuova email con le credenziali.`)) return;
    try {
      const res = await fetch(`${API}/api/clinics/${clinicId}/users/${userId}/reset-password`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message); }
      else { toast.error(data.detail || 'Errore'); }
    } catch { toast.error('Errore di rete'); }
  };

  const handleImpersonateUser = async (userId, userName, role) => {
    try {
      const res = await fetch(`${API}/api/auth/impersonate/${userId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        const portalUrl = `${window.location.origin}/login?impersonate_token=${data.access_token}`;
        window.open(portalUrl, '_blank');
        toast.success(`Accesso come ${userName} aperto in una nuova scheda`);
      } else { toast.error(data.detail || 'Errore'); }
    } catch { toast.error('Errore di rete'); }
  };

  const handleImpersonateDoctor = async (doctorId, doctorName) => {
    try {
      const res = await fetch(`${API}/api/auth/impersonate-doctor/${doctorId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        const portalUrl = `${window.location.origin}/medico?impersonate_token=${data.access_token}`;
        window.open(portalUrl, '_blank');
        toast.success(`Accesso come Dr. ${doctorName} aperto in una nuova scheda`);
      } else { toast.error(data.detail || 'Errore'); }
    } catch { toast.error('Errore di rete'); }
  };

  const handleImpersonateAuto = async (userId, userName, role) => {
    if (role === 'doctor') {
      // Find doctor_id from user and open doctor portal
      try {
        // First get the user's linked doctor record
        const res = await fetch(`${API}/api/auth/impersonate/${userId}`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.doctor_id) {
          handleImpersonateDoctor(data.doctor_id, userName);
        } else {
          // Fallback: open main dashboard
          if (res.ok) {
            const portalUrl = `${window.location.origin}/login?impersonate_token=${data.access_token}`;
            window.open(portalUrl, '_blank');
            toast.success(`Accesso come ${userName} aperto in una nuova scheda`);
          } else { toast.error(data.detail || 'Errore'); }
        }
      } catch { toast.error('Errore di rete'); }
    } else {
      handleImpersonateUser(userId, userName, role);
    }
  };



  const openEdit = (c) => {
    setForm({ name: c.name, address: c.address || '', city: c.city || '', phone: c.phone || '', email: c.email || '', phone_number: c.phone_number || '', transfer_number: c.transfer_number || '' });
    setEditClinic(c);
    setShowCreate(true);
  };

  if (loading) return (
    <Layout><div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" /></div></Layout>
  );

  return (
    <Layout>
      <div className="content-area" data-testid="clinics-page">
        <div className="max-w-5xl mx-auto space-y-6">

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Gestione Cliniche</h1>
              <p className="text-sm text-slate-500 mt-1">{clinics.length} {clinics.length === 1 ? 'clinica' : 'cliniche'} registrate</p>
            </div>
            {(user?.role === 'superadmin' || user?.role === 'partner') && (
              <button onClick={() => { setShowCreate(true); setEditClinic(null); setForm({ name: '', address: '', city: '', phone: '', email: '', phone_number: '' }); }}
                className="flex items-center gap-2 px-4 py-2.5 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors text-sm font-medium shrink-0" data-testid="create-clinic-btn">
                <Plus className="w-4 h-4" /> Nuova Clinica
              </button>
            )}
          </div>

          {/* Create/Edit Modal */}
          {showCreate && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto" data-testid="clinic-form-modal">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-lg text-slate-800">{editClinic ? 'Modifica Clinica' : 'Nuova Clinica'}</h2>
                  <button onClick={() => { setShowCreate(false); setEditClinic(null); }}><X className="w-5 h-5 text-slate-400" /></button>
                </div>
                <div className="space-y-3">
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome clinica *"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-sky-500" data-testid="clinic-name-input" />
                  <div className="grid grid-cols-2 gap-3">
                    <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Indirizzo"
                      className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-sky-500" />
                    <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Citta"
                      className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-sky-500" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefono"
                      className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-sky-500" />
                    <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email"
                      className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Numero TelcaVoIP AI</label>
                    <select
                      value={form.phone_number}
                      onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 bg-white"
                      data-testid="voicebook-number-select"
                    >
                      <option value="">-- Seleziona numero --</option>
                      {/* Show current number if editing */}
                      {editClinic && form.phone_number && !availableNumbers.find(n => n.number === form.phone_number) && (
                        <option value={form.phone_number}>{form.phone_number} (attuale)</option>
                      )}
                      {availableNumbers.map(n => (
                        <option key={n.number} value={n.number}>{n.number}{n.label && !n.label.includes(n.number) ? ` - ${n.label}` : ''}</option>
                      ))}
                      {availableNumbers.length === 0 && !form.phone_number && (
                        <option disabled>Nessun numero disponibile</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Numero Operatore (trasferimento)</label>
                    <input value={form.transfer_number} onChange={(e) => setForm({ ...form, transfer_number: e.target.value })} placeholder="Es. +39011234567 (reception)"
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-sky-500" data-testid="transfer-number-input" />
                    <p className="text-[10px] text-slate-400 mt-1">Numero a cui trasferire la chiamata quando l'AI non riesce a gestire o il paziente lo richiede</p>
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <button onClick={() => { setShowCreate(false); setEditClinic(null); }} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Annulla</button>
                  <button onClick={handleSave} className="px-6 py-2 bg-sky-600 text-white rounded-lg text-sm font-medium hover:bg-sky-700" data-testid="save-clinic-btn">
                    {editClinic ? 'Aggiorna' : 'Crea Clinica'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Clinics List */}
          {clinics.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">Nessuna clinica registrata</p>
            </div>
          ) : (
            <div className="space-y-4">
              {clinics.map((c) => (
                <div key={c.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`clinic-${c.id}`}>
                  {/* Trial/Demo Banner */}
                  {c.trial_mode && (
                    <div className={`px-5 py-2.5 flex items-center justify-between ${
                      c.trial_days_left <= 0 ? 'bg-red-500 text-white' :
                      c.trial_days_left <= 2 ? 'bg-red-50 border-b border-red-200 text-red-700' :
                      'bg-amber-50 border-b border-amber-200 text-amber-700'
                    }`} data-testid={`trial-banner-${c.id}`}>
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <span className={`w-2 h-2 rounded-full animate-pulse ${c.trial_days_left <= 0 ? 'bg-white' : c.trial_days_left <= 2 ? 'bg-red-500' : 'bg-amber-500'}`} />
                        {c.trial_days_left <= 0 ? 'DEMO SCADUTA' : 'MODALITA\' DEMO'}
                      </div>
                      <div className="flex items-center gap-3">
                        {c.trial_days_left > 0 && (
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-1.5 bg-white/30 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{
                                width: `${Math.max(5, (c.trial_days_left / 14) * 100)}%`,
                                backgroundColor: c.trial_days_left <= 2 ? '#ef4444' : '#f59e0b'
                              }} />
                            </div>
                            <span className="text-xs font-bold">{c.trial_days_left} giorni rimasti</span>
                          </div>
                        )}
                        {c.trial_days_left <= 0 && <span className="text-xs font-bold">Attiva l'abbonamento per continuare</span>}
                      </div>
                    </div>
                  )}
                  {/* Clinic Header */}
                  <div className="p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      <div className="flex items-center gap-4 cursor-pointer flex-1 min-w-0" onClick={() => setExpandedClinic(expandedClinic === c.id ? null : c.id)}>
                        <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center shrink-0">
                          <Building2 className="w-6 h-6 text-sky-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="font-semibold text-slate-800">{c.name}</h3>
                            {c.trial_mode ? (
                              <span className={`px-2 py-0.5 text-xs rounded-full ${c.trial_days_left > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                {c.trial_days_left > 0 ? `Prova - ${c.trial_days_left}g rimasti` : 'Prova scaduta'}
                              </span>
                            ) : (
                              <span className={`px-2 py-0.5 text-xs rounded-full ${c.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                {c.is_active ? 'Attiva' : 'Inattiva'}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-slate-400 flex-wrap">
                            {c.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.city}</span>}
                            {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                            {c.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{c.email}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        {/* Stats */}
                        <div className="grid grid-cols-4 gap-3 text-center">
                          <div><span className="text-lg font-bold text-sky-600">{c.stats?.departments || 0}</span><p className="text-xs text-slate-400">Reparti</p></div>
                          <div><span className="text-lg font-bold text-emerald-600">{c.stats?.doctors || 0}</span><p className="text-xs text-slate-400">Medici</p></div>
                          <div><span className="text-lg font-bold text-violet-600">{c.stats?.patients || 0}</span><p className="text-xs text-slate-400">Pazienti</p></div>
                          <div><span className="text-lg font-bold text-amber-600">{c.stats?.appointments || 0}</span><p className="text-xs text-slate-400">Appunt.</p></div>
                        </div>
                        {(user?.role === 'superadmin' || user?.role === 'partner') && (
                          <div className="flex gap-1">
                            <button onClick={() => openEdit(c)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid={`edit-clinic-${c.id}`}>
                              <Pencil className="w-4 h-4 text-slate-400" />
                            </button>
                            <button onClick={() => handleDelete(c.id, c.name)} className="p-2 hover:bg-red-50 rounded-lg" data-testid={`delete-clinic-${c.id}`}>
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        )}
                        <button onClick={() => setExpandedClinic(expandedClinic === c.id ? null : c.id)}>
                          {expandedClinic === c.id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedClinic === c.id && (
                    <div className="border-t border-slate-100 p-5 bg-slate-50 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div><span className="text-slate-400">Indirizzo:</span> <span className="text-slate-700">{c.address || '-'}</span></div>
                        <div><span className="text-slate-400">Numero TelcaVoIP AI:</span> <span className="text-slate-700 font-mono">{c.phone_number || 'Non assegnato'}</span></div>
                        <div><span className="text-slate-400">Numero Operatore:</span> <span className="text-slate-700 font-mono">{c.transfer_number || 'Non configurato'}</span></div>
                      </div>

                      {/* Chat toggle */}
                      {user?.role === 'superadmin' && (
                        <ChatToggle clinicId={c.id} token={token} />
                      )}

                      {/* Users */}
                      {/* Users section - always show */}
                      <div>
                        <h4 className="font-medium text-slate-700 text-sm mb-2 flex items-center gap-2"><Users className="w-4 h-4" /> Utenti ({c.users?.length || 0})</h4>
                        {c.users && c.users.length > 0 ? (
                          <div className="space-y-2">
                            {c.users.map((u) => (
                              <div key={u.id} className="bg-white p-3 rounded-lg border border-slate-200">
                                {editingUser?.id === u.id ? (
                                  <div className="space-y-2">
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                      <input value={editingUser.name} onChange={e => setEditingUser({...editingUser, name: e.target.value})}
                                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Nome" />
                                      <select value={editingUser.role} onChange={e => setEditingUser({...editingUser, role: e.target.value})}
                                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
                                        <option value="receptionist">Receptionist</option>
                                        <option value="doctor">Medico</option>
                                        <option value="admin">Admin</option>
                                      </select>
                                      <input value={editingUser.newPassword || ''} onChange={e => setEditingUser({...editingUser, newPassword: e.target.value})}
                                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Nuova password (opzionale)" />
                                    </div>
                                    <div className="flex gap-2">
                                      <button onClick={() => handleUpdateUser(c.id, u.id)} className="px-3 py-1.5 bg-sky-600 text-white rounded-lg text-xs hover:bg-sky-700">Salva</button>
                                      <button onClick={() => setEditingUser(null)} className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg text-xs">Annulla</button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <span className="font-medium text-slate-700 text-sm">{u.name || u.email}</span>
                                    <span className="text-xs text-slate-400 truncate">{u.email}</span>
                                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                                      u.role === 'receptionist' ? 'bg-emerald-100 text-emerald-700' :
                                      u.role === 'doctor' ? 'bg-blue-100 text-blue-700' :
                                      u.role === 'admin' ? 'bg-sky-100 text-sky-700' :
                                      'bg-slate-100 text-slate-600'
                                    }`}>{
                                      u.role === 'receptionist' ? 'Receptionist' :
                                      u.role === 'doctor' ? 'Medico' : u.role
                                    }</span>
                                    {(user?.role === 'superadmin' || user?.role === 'partner') && u.id !== user?.id && (
                                      <div className="flex gap-1 ml-auto">
                                        {user?.role === 'superadmin' && (
                                          <button onClick={() => handleImpersonateAuto(u.id, u.name || u.email, u.role)}
                                            className="p-1.5 hover:bg-sky-50 rounded-lg" title={`Accedi come ${u.name || u.email}`} data-testid={`impersonate-${u.id}`}>
                                            <LogIn className="w-3.5 h-3.5 text-sky-500" />
                                          </button>
                                        )}
                                        <button onClick={() => handleResetPassword(c.id, u.id, u.name || u.email)}
                                          className="p-1.5 hover:bg-amber-50 rounded-lg" title="Reinvia email con nuova password" data-testid={`reset-pw-${u.id}`}>
                                          <RotateCcw className="w-3.5 h-3.5 text-amber-500" />
                                        </button>
                                        <button onClick={() => setEditingUser({id: u.id, name: u.name || '', role: u.role, newPassword: ''})}
                                          className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid={`edit-user-${u.id}`}>
                                          <Pencil className="w-3.5 h-3.5 text-slate-400" />
                                        </button>
                                        <button onClick={() => handleDeleteUser(c.id, u.id, u.name || u.email)}
                                          className="p-1.5 hover:bg-red-50 rounded-lg" data-testid={`delete-user-${u.id}`}>
                                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-400 italic">Nessun utente aggiunto</p>
                        )}
                      </div>

                      {/* Add User */}
                      {(user?.role === 'superadmin' || user?.role === 'partner') && (
                        <div>
                          {showAddUser === c.id ? (
                            <div className="bg-white p-4 rounded-lg border border-slate-200 space-y-3">
                              <h4 className="font-medium text-sm text-slate-700">Nuovo utente per {c.name}</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs text-slate-500 mb-1">Nome</label>
                                  <input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} placeholder="Mario Rossi"
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="user-name-input" />
                                </div>
                                <div>
                                  <label className="block text-xs text-slate-500 mb-1">Email *</label>
                                  <input value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} placeholder="utente@clinica.it"
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="user-email-input" />
                                </div>
                                <div>
                                  <label className="block text-xs text-slate-500 mb-1">Password *</label>
                                  <div className="relative">
                                    <input type={showUserPassword ? 'text' : 'password'} value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} placeholder="Minimo 8 caratteri"
                                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm pr-10" data-testid="user-password-input" />
                                    <button type="button" onClick={() => setShowUserPassword(!showUserPassword)}
                                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600" data-testid="toggle-password-visibility">
                                      {showUserPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                  </div>
                                  <p className="text-[10px] text-slate-400 mt-1">L'utente dovrà cambiarla al primo accesso</p>
                                </div>
                                <div>
                                  <label className="block text-xs text-slate-500 mb-1">Ruolo</label>
                                  <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="user-role-select">
                                    <option value="receptionist">Receptionist</option>
                                    <option value="doctor">Medico</option>
                                    <option value="admin">Admin</option>
                                  </select>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button onClick={() => handleAddUser(c.id)} className="px-4 py-2 bg-sky-600 text-white rounded-lg text-sm hover:bg-sky-700" data-testid="save-user-btn">Crea Utente</button>
                                <button onClick={() => setShowAddUser(null)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">Annulla</button>
                              </div>
                            </div>
                          ) : (
                            <button onClick={() => setShowAddUser(c.id)}
                              className="flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700" data-testid={`add-user-btn-${c.id}`}>
                              <UserPlus className="w-4 h-4" /> Aggiungi utente
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
